// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";

//Backend access
import PropTypes from "prop-types";
import bksAPI from 'services/api/bks';
import reportsAPI from 'services/api/reports';
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import CameraLatest from "../CameraLatest";
import { Card, Grid, Tooltip } from "@mui/material";
import moment from "moment";
import Divider from "@material-ui/core/Divider";
import SoftButton from "components/SoftComponents/SoftButton";
import Swal from "sweetalert2";
import fileDownload from "js-file-download";
import { getUserAccessLevel } from "utils/setup";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { useMemo, useState } from "react";
import theme from "assets/theme";
import { loadingScreen } from "utils/wrapper";
import ParkingValidation from "assets/json/ParkingValidation.json";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import LiveView from "components/LiveView";

const getCurrentZone = (device, timestamp) => {
    const m = moment(timestamp * 1000).tz(ParkingValidation['timezone']);
    const day = m.day(); // 0 (Sun) to 6 (Sat)
    const currentTime = m.format("HH:mm");

    const restrictions = ParkingValidation["locations"][device.stop.site_id]["restrictions"];

    for (var zone in restrictions) {
        if (restrictions[zone].active_days.indexOf(day) > -1) {
            if (typeof restrictions[zone].start_time == 'undefined' && typeof restrictions[zone].end_time == "undefined") {
                return restrictions[zone];
            }

            var end_time = restrictions[zone].end_time == "00:00" ? "24:00" : restrictions[zone].end_time;
            if ((typeof restrictions[zone].start_time == 'undefined' && typeof restrictions[zone].end_time == "undefined") || (currentTime >= restrictions[zone].start_time && currentTime < end_time)) {
                return restrictions[zone];
            }
        }
    }
}

const checkWithinArea = (device, dimensions) => {
    const threshold = 75;
    const line = ParkingValidation["locations"][device.stop.site_id]["line"];
    var { start_x, start_y, width, height } = dimensions;

    const box = {
        'x1': start_x,
        'y1': start_y,
        'x2': start_x + width,
        'y2': start_y + height
    };

    const middle_height = height / 3;
    const y1_hits_middle = box.y1 + middle_height;
    const y2_hits_middle = box.y1 + (3 * middle_height);

    const overlap_x_start = Math.max(line.x1, box.x1);
    const overlap_x_end = Math.min(line.x2, box.x2);

    if (overlap_x_end <= overlap_x_start) {
        return false;
    }

    const horizontal_overlap = overlap_x_end - overlap_x_start;
    const coverage_percent = (horizontal_overlap / width) * 100;
    // console.log(coverage_percent)

    const m = (line.y2 - line.y1) / (line.x2 - line.x1);
    const b = line.y1 - m * line.x1;

    const y_at_overlap_start = m * overlap_x_start + b;
    const y_at_overlap_end = m * overlap_x_end + b;

    const segment_min_y = Math.min(y_at_overlap_start, y_at_overlap_end);
    const segment_max_y = Math.max(y_at_overlap_start, y_at_overlap_end);

    const intersects_middle_third = segment_max_y >= y1_hits_middle && segment_min_y <= y2_hits_middle;
    return intersects_middle_third && coverage_percent >= threshold;
}

const formatTimeParked = (vehicle, zone = null) => {
    var seen_str = "";
    var colour = null;

    var time_parked = vehicle.last_seen - vehicle.timestamp;

    if (!time_parked) {
        return false;
    }

    if (time_parked >= 3600) {
        seen_str = `${Math.floor(time_parked / 3600)} hr, ${Math.floor((time_parked % 3600) / 60)} mins`
    } else {
        seen_str = `${Math.floor((time_parked % 3600) / 60)} mins`
    }

    if (zone == null) {
        return seen_str;
    }

    if (vehicle.type == "truck") {
        if ((time_parked / 60) > zone['limits']['commercial_max_minutes']) {
            colour = "#ff0000";
        }
    }
    else if ((time_parked / 60) > zone['limits']['passenger_max_minutes']) {
        colour = "#ff0000";
    }

    return { seen_str, colour };
}

function determineIfParked(zone, objects, device) {
    var overstay_count = 0;
    var occupied = false;
    var monitoring = [];

    for (var obj of objects) {
        if (obj.type != "bus" && checkWithinArea(device, obj.dimensions)) {
            monitoring.push(obj);

            if (zone['limits']) {
                var time_parked = obj.last_seen - obj.timestamp;
                // console.log(time_parked, obj.type)
                if (obj.type == "truck") {
                    occupied = true;
                    if ((time_parked / 60) > zone['limits']['commercial_max_minutes']) {
                        overstay_count++;
                    }
                }
                else if ((time_parked / 60) > zone['limits']['passenger_max_minutes']) {
                    occupied = true;
                    overstay_count++;
                }
            }
        }
    }

    return { overstay_count, occupied, monitoring };
}

function OccupancyFeed({ device, user, deviceLayout }) {
    bksAPI.loadToken(useAuth0());
    reportsAPI.loadToken(useAuth0());

    const [reportStartTime, setReportStartTime] = useState(moment().tz(ParkingValidation['timezone']).startOf('day').unix());
    const [dataStartTime] = useState(moment().startOf('day').unix());
    const [dataEndTime] = useState(moment().unix());

    const latest_objects = useQuery({
        queryKey: ['getLatestRawCameraObjects', device.uuid],
        queryFn: async () => {
            const data = await bksAPI.getLatestRawCameraObjects([device.uuid]);
            return data;
        },
        refetchInterval: 150 * 1000,
        staleTime: 60 * 1000
    });

    const history_objects = useQuery({
        queryKey: ['getRawCameraObjectsHistory', device.uuid, dataStartTime, dataEndTime],
        queryFn: async () => {
            const data = await bksAPI.getRawCameraObjectsHistory([device.uuid], dataStartTime, dataEndTime);
            return data;
        },
        staleTime: 150 * 1000
    });

    if (latest_objects.error || history_objects.error) {
        return "Error!";
    }


    const latest_data = useMemo(() => {
        // Latest camera image
        var { timestamp, objects } = latest_objects.data ? latest_objects.data[device.uuid] : { timestamp: moment().unix(), objects: [] };
        const zone = getCurrentZone(device, timestamp);
        const { overstay_count, occupied, monitoring } = determineIfParked(zone, objects, device);

        return { overstay_count, occupied, monitoring, zone, timestamp };
    }, [latest_objects.data, device.uuid]);

    const illegally_parked = useMemo(() => {
        // Historical camera images (since midnight)
        var history = history_objects.data ? history_objects.data[device.uuid].map((elem) => {
            return {
                ...elem,
                objects: elem.objects.filter((obj) => obj.type != "bus" && obj.repeats != 0)
            }
        }).filter((elem) => elem.objects.length != 0).sort((a, b) => b.timestamp - a.timestamp) : [];

        var illegally_parked_dict = {};
        for (var elem of history) {
            for (var obj of elem.objects) {
                if (typeof illegally_parked_dict[obj._id] == 'undefined') {
                    const timestamp_zone = getCurrentZone(device, elem.timestamp);
                    const is_parked = determineIfParked(timestamp_zone, [obj], device);

                    if (is_parked.overstay_count && is_parked.occupied) {
                        illegally_parked_dict[obj._id] = obj;
                    }
                }
            }
        }

        return Object.values(illegally_parked_dict).sort((a, b) => b.repeats - a.repeats);

    }, [history_objects.data, device.uuid]);

    async function downloadPDF() {
        Swal.showLoading();

        const options = {
            responseType: 'blob',
            onDownloadProgress: function (progressEvent) {
                const percentComplete = Math.floor((progressEvent.loaded / progressEvent.total) * 100);
                if (percentComplete == 100) {
                    Swal.close();
                }
            }
        }

        var reportEndTime = moment(reportStartTime * 1000).tz(ParkingValidation['timezone']).add(1, 'day').startOf('day').unix();
        reportsAPI.getCameraObjectsReport([device.uuid], reportStartTime, reportEndTime, options).then(function (data) {
            var formatted_start_time = moment(reportStartTime * 1000).tz(ParkingValidation['timezone']).format('DDMM');

            fileDownload(data, `${device.name}_${formatted_start_time}.pdf`);
        });
    };

    const generateReport = (
        <>
            <SoftBox display="flex" justifyContent="center" alignItems="center" py={{ xxs: 0, sm: 1 }} pr={{ xxs: 0, x3l: 1 }}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker format="DD/MM/YYYY" value={moment(reportStartTime * 1000).tz(ParkingValidation['timezone'])} minDate={moment("YYYY-MM-DD", "2026-03-01").tz(ParkingValidation['timezone'])} maxDate={moment().tz(ParkingValidation['timezone'])} onChange={(new_value) => { setReportStartTime(new_value.tz(ParkingValidation['timezone']).unix()) }} />
                </LocalizationProvider>
                <SoftBox display={theme.breakpoints.up("x3l") ? 'none' : 'flex'}>
                    <SoftButton variant="gradient" color="info" onClick={() => { downloadPDF(); }}>Generate Report</SoftButton>
                </SoftBox>
            </SoftBox>
            <SoftBox display={theme.breakpoints.up("x3l") ? 'flex' : 'none'}>
                <SoftButton variant="gradient" color="info" onClick={() => { downloadPDF(); }}>Generate Report</SoftButton>
            </SoftBox>
        </>
    );

    return (
        <Card sx={{ height: '100%', width: '100%', background: '#f9f9f9' }}>
            <SoftBox width="100%" height="100%" display="flex" justifyContent="center" alignItems="center">
                <Card sx={{ height: '90%', width: '90%', overflowY: 'auto', padding: '8px', marginTop: deviceLayout == 'mobile' && '3%', marginBottom: deviceLayout == 'mobile' && '3%' }}>
                    <SoftBox p={1} mt={deviceLayout == 'mobile' ? 1 : 0} mb={deviceLayout == 'mobile' ? 1 : 0} display="flex" flexDirection="column" height="100%" sx={{ minHeight: 0 }}>
                        <SoftBox alignItems='left' display="flex" flexDirection="column" flexShrink={0}>
                            <SoftBox display="flex" flexDirection={{ xxs: 'column', sm: "row" }} justifyContent="space-between" alignItems={{ xxs: "center", sm: 'normal' }} mb={1}>
                                <SoftBox display="flex" flexDirection="column" justifyContent={{ xxs: 'center', sm: "space-between" }} alignItems={{ xxs: 'center', sm: 'flex-start' }} mt={{ xxs: 0, sm: 1 }} mb={{ xxs: 3, sm: 0 }}>
                                    <SoftTypography variant={"h3"} fontWeight="bold">Current Condition</SoftTypography>
                                    <SoftBox display={{ xxs: "none", sm: 'flex' }} flexDirection={{ xxs: 'column', x3l: 'row' }} alignItems={{ xxs: "flex-start", x3l: "center" }} justifyContent="center">
                                        {generateReport}
                                    </SoftBox>
                                </SoftBox>
                                <SoftBox width="100%" maxWidth='200px'>
                                    <LiveView device={{ ...device, overlay_id: 'translink-kerbside', uuid: device.uuid == "3d001f-31335102-30313433" ? "35001f-3133510a-30313433" : "2a0029-3233510b-38373433" }} nexus_id={'translink_nucleus'} deviceLayout={deviceLayout} isPerspectiveLiveview={true} perspectiveAdjustment={true} />
                                </SoftBox>
                            </SoftBox>
                            <SoftBox py={2}>
                                <Divider orientation="horizontal" />
                            </SoftBox>
                            <SoftBox display={{ xxs: "flex", sm: 'none' }} flexDirection='column' justifyContent="center" alignItems="center">
                                <SoftBox display="flex" flexDirection={{ xxs: 'column', xs: "row" }} sx={{ gap: 1 }} alignItems="center">
                                    {generateReport}
                                </SoftBox>
                                <SoftBox display="flex" width="100%" py={2}>
                                    <Divider orientation="horizontal" style={{ width: '100%' }} />
                                </SoftBox>
                            </SoftBox>
                            <SoftBox display="flex" flexDirection={{ xxs: "column", md: "row" }} justifyContent="space-between" flex={1}>
                                <SoftBox display="flex" flex={{ xxs: 6 }} flexDirection="column" alignItems={{ xxs: 'center', md: "flex-start" }} px={1}>
                                    <SoftTypography variant="h5">Occupied History</SoftTypography>
                                    <SoftTypography variant="caption">Since Midnight</SoftTypography>
                                    <SoftBox pt={1}>
                                        {illegally_parked.length > 0 && (
                                            <SoftTypography variant="h6">Longest Parking Offence: {formatTimeParked(illegally_parked[0])}</SoftTypography>
                                        )}
                                        <SoftTypography variant="h6">Total Parking Offences: {illegally_parked.length}</SoftTypography>
                                    </SoftBox>
                                </SoftBox>
                                <SoftBox>
                                    <Divider orientation="vertical" />
                                </SoftBox>
                                <SoftBox py={2}>
                                    <Divider orientation="horizontal" />
                                </SoftBox>
                                <SoftBox display="flex" flex={{ xxs: 6 }} flexDirection="column" alignItems={{ xxs: 'center', md: "flex-end" }} px={1}>
                                    <SoftBox display="flex" flexDirection="row">
                                        <SoftTypography variant="h5">Monitoring {latest_data.monitoring.length} {latest_data.monitoring.length == 1 ? 'Vehicle' : 'Vehicles'}</SoftTypography>
                                        <Tooltip title={<div>Monitored vehicles pass through the striped identification line (coloured as black and yellow).<br /><br /> Other vehicles are ignored and classified as "passing by".</div>} placement="right" sx={{ paddingTop: '2px', paddingLeft: '4px' }}>
                                            <HelpOutlineIcon fontSize="small" />
                                        </Tooltip>
                                    </SoftBox>
                                    {latest_data.monitoring.sort((a, b) => (b.last_seen - b.timestamp) - (a.last_seen - a.timestamp)).map((vehicle, index) => {
                                        var { seen_str, colour } = formatTimeParked(vehicle, latest_data.zone);
                                        var vehicle_type = vehicle.type.charAt(0).toUpperCase() + vehicle.type.slice(1);

                                        return (
                                            <SoftBox key={index}>
                                                {!seen_str ? (
                                                    <SoftTypography variant="h6">Just parked</SoftTypography>
                                                ) : (
                                                    <SoftTypography variant="h6" sx={{ color: colour != null && colour }}>{vehicle_type} parked for {seen_str}</SoftTypography>
                                                )}
                                            </SoftBox>
                                        )
                                    })}
                                </SoftBox>
                            </SoftBox>
                            <SoftBox py={2}>
                                <Divider orientation="horizontal" />
                            </SoftBox>
                        </SoftBox>
                        <CameraLatest uuid={device.uuid} refetch={150 * 1000} zoom={deviceLayout != 'mobile'} line_coordinates={ParkingValidation["locations"][device.stop.site_id]["line"]} />
                    </SoftBox>
                </Card>
            </SoftBox>
        </Card>
    )
}

// Typechecking props for the OccupancyFeed
OccupancyFeed.propTypes = {
    device: PropTypes.object,
    user: PropTypes.object,
    deviceLayout: PropTypes.string
};

export default OccupancyFeed;
